import { useEffect } from 'react';
import { COLORS } from '../lib/theme';
import { Icon } from './Icon';

export interface FilePreviewPopupProps {
  fileName: string;
  /**
   * The publicly resolvable URL the embed iframe should load. For Firebase
   * Storage objects this is the `getDownloadURL()` result (carries an auth
   * token in the query string).
   *
   * `null` while the URL is still being resolved by the parent — the popup
   * renders a loading state instead of trying to embed.
   */
  url: string | null;
  /** Optional MIME type to pick the right preview backend. */
  mimeType: string | null;
  /** Error from the URL resolver, if any. Falls back to the download CTA. */
  error?: string | null;
  onClose: () => void;
}

/**
 * Nested overlay that previews an attachment file inside the hunt invitation
 * modal. PDFs and images embed directly from Firebase Storage. Everything
 * else — including Office documents, which no browser renders natively —
 * falls back to a "Download" CTA so users can open the file in the OS
 * application of their choice.
 *
 * Office formats deliberately have NO inline preview. They previously
 * embedded Google Docs Viewer, which meant handing Google the Firebase
 * download URL — a bearer token granting access to a private team document —
 * so that Google's servers could fetch and cache the file. That is a document
 * leaving our infrastructure for a third party, for a preview.
 *
 * It did not even work reliably: when Google failed to render, it painted its
 * own "Could not preview the file" message inside the iframe. Being
 * cross-origin, that failure is undetectable from here, so the download
 * fallback below never ran and the user hit a dead end with someone else's
 * error on screen. Restoring an inline preview means rendering the format
 * ourselves (e.g. SheetJS), not re-adding a third-party embed.
 *
 * Presentational only — the parent owns URL resolution (Firebase Storage)
 * so this stays free of any cloud-SDK coupling and can be unit tested with
 * fake URLs.
 */
export function FilePreviewPopup({ fileName, url, mimeType, error, onClose }: FilePreviewPopupProps) {
  // Close on Escape — matches the parent modal's UX.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const kind = inferKind(fileName, mimeType);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${fileName}`}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          width: 'min(1100px, 100%)',
          height: 'min(820px, 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
            gap: 12,
          }}
        >
          <span
            title={fileName}
            style={{
              fontWeight: 700,
              fontSize: 14,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fileName}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="wh-btn"
                style={{ padding: '6px 12px', fontSize: 12 }}
                download={fileName}
              >
                Download
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="wh-btn"
              style={{ padding: '6px 10px', fontSize: 12 }}
            >
              <Icon name="close" size={14} />
            </button>
          </div>
        </div>
        <div style={{ flex: 1, background: COLORS.surfaceContainerLowest, position: 'relative' }}>
          {renderBody({ url, kind, fileName, error })}
        </div>
      </div>
    </div>
  );
}

type PreviewKind = 'pdf' | 'office' | 'image' | 'other';

function inferKind(fileName: string, mimeType: string | null): PreviewKind {
  const lowerMime = (mimeType ?? '').toLowerCase();
  if (lowerMime === 'application/pdf') return 'pdf';
  if (lowerMime.startsWith('image/')) return 'image';
  if (
    lowerMime === 'application/msword' ||
    lowerMime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    lowerMime === 'application/vnd.ms-excel' ||
    lowerMime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return 'office';
  }
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx', 'xls', 'xlsx'].includes(ext)) return 'office';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  return 'other';
}

function renderBody(args: { url: string | null; kind: PreviewKind; fileName: string; error?: string | null }) {
  const { url, kind, fileName, error } = args;
  if (error) {
    return (
      <Centered>
        <p style={{ marginBottom: 12 }}>
          Couldn't load preview: <strong>{error}</strong>
        </p>
      </Centered>
    );
  }
  if (!url) {
    return <Centered>Loading preview…</Centered>;
  }
  if (kind === 'pdf') {
    return (
      <iframe
        title={fileName}
        src={url}
        style={{ border: 'none', width: '100%', height: '100%' }}
      />
    );
  }
  if (kind === 'image') {
    return (
      <Centered>
        <img
          src={url}
          alt={fileName}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      </Centered>
    );
  }
  // 'office' deliberately has no branch and falls through to the download CTA
  // below — see the note on the component above. The kind is still inferred
  // because it names the one format family a future in-house renderer would
  // target; it is not an unhandled case waiting to be filled in with an embed.
  return (
    <Centered>
      <p style={{ marginBottom: 12 }}>
        Inline preview isn't supported for this file type.
      </p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="wh-btn wh-btn-primary"
        style={{ padding: '8px 16px' }}
        download={fileName}
      >
        Open file
      </a>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 24,
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}
